import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Heart, LogOut, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { getMyActivity, sendMeDigest } from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function Profile() {
  const { user, loading, logout, login } = useAuth();
  const navigate = useNavigate();
  const [activity, setActivity] = useState({ posts: [], replies: [] });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user) getMyActivity().then(setActivity).catch(() => {});
  }, [user]);

  const doLogout = async () => { await logout(); navigate('/'); };

  const emailDigest = async () => {
    setSending(true);
    try {
      const res = await sendMeDigest({ origin_url: window.location.origin });
      toast.success(res.message || 'Digest sent!');
    } catch (e) { toast.error('Could not send digest.'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-5 pt-20 text-center text-[#5B6472]">Loading...</div>;

  if (!user) return (
    <div className="max-w-md mx-auto px-5 pt-24 text-center">
      <h1 className="font-display font-extrabold text-[#0E1726] text-[28px]">Sign in to your profile</h1>
      <p className="text-[#5B6472] mt-3">See your discussions, replies and manage your digest.</p>
      <button onClick={login} className="mt-6 bg-[#2E6BFF] text-white font-semibold px-6 py-3 rounded-full">Continue with Google</button>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-12">
      <div className="bg-white rounded-[24px] p-7 sm:p-9 shadow-[0_25px_70px_-48px_rgba(14,23,38,0.4)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <span className="w-16 h-16 rounded-full bg-[#2E6BFF]/12 text-[#2E6BFF] font-bold text-xl flex items-center justify-center">{user.name?.charAt(0)}</span>
          )}
          <div>
            <h1 className="font-display font-extrabold text-[#0E1726] text-[24px]">{user.name}</h1>
            <p className="text-[#5B6472] flex items-center gap-1.5 text-sm mt-0.5"><Mail size={14} /> {user.email}</p>
          </div>
        </div>
        <div className="flex gap-2.5">
          <button onClick={emailDigest} disabled={sending} className="bg-[#2E6BFF] text-white font-semibold px-4 py-2.5 rounded-full inline-flex items-center gap-2 text-sm hover:bg-[#1f5aef] disabled:opacity-60">
            <Send size={15} /> {sending ? 'Sending...' : 'Email me the digest'}
          </button>
          <button onClick={doLogout} className="border border-black/15 text-[#0E1726] font-semibold px-4 py-2.5 rounded-full inline-flex items-center gap-2 text-sm hover:bg-black/5">
            <LogOut size={15} /> Log out
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div>
          <h2 className="font-display font-bold text-[#0E1726] text-[19px] mb-4">Your discussions ({activity.posts.length})</h2>
          <div className="space-y-3">
            {activity.posts.length === 0 && <p className="text-[#5B6472]">You haven't posted yet.</p>}
            {activity.posts.map((p) => (
              <Link key={p.id} to={`/community/${p.id}`} className="block bg-white rounded-xl p-5 shadow-[0_20px_50px_-46px_rgba(14,23,38,0.4)] hover:-translate-y-0.5 transition-transform">
                <h3 className="font-semibold text-[#0E1726]">{p.title}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-[#5B6472]">
                  <span className="flex items-center gap-1.5"><MessageSquare size={14} /> {p.reply_count}</span>
                  <span className="flex items-center gap-1.5"><Heart size={14} /> {p.likes}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display font-bold text-[#0E1726] text-[19px] mb-4">Your replies ({activity.replies.length})</h2>
          <div className="space-y-3">
            {activity.replies.length === 0 && <p className="text-[#5B6472]">No replies yet.</p>}
            {activity.replies.map((r) => (
              <Link key={r.id} to={`/community/${r.post_id}`} className="block bg-white rounded-xl p-5 shadow-[0_20px_50px_-46px_rgba(14,23,38,0.4)] hover:-translate-y-0.5 transition-transform">
                <p className="text-[#334155] line-clamp-2">{r.content}</p>
                <p className="text-xs text-[#5B6472] mt-2">on “{r.post_title}”</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
